<script lang="ts">
    import {
        austinForProfit,
        austinNonProfit,
        otherForProfit,
        otherNonProfit
    } from "./data.js";

    let propertyType: string = '';
    let energyProvider: string = 'Austin Energy';
    let profitStatus: string = "For-Profit";
    let averageMonthlyBill: number = 1;
    let response: Object = {};

    let averageMonthlyBillLabels: string[] = [
        'Less than $1000',
        '$1000-$2500',
        '$2500-$5000',
        '$5000-$10000',
        '$10000+'
    ];

    function setProperty(value: string) {
        propertyType = value;
    }

    function onSubmit() {
        let data: object = {};
        if(energyProvider === 'Austin Energy') {
            data = (profitStatus === 'For-Profit') ? austinForProfit.records : austinNonProfit.records;
        } else {
            data = (profitStatus === 'For-Profit') ? otherForProfit.records : otherNonProfit.records;
        }

        response = data[averageMonthlyBill-1];
    }
</script>

<div class="pricing-table">
    <div id="sv-questionnaire-property-type">
        <span class="label">What type of property do you have?</span>

        <div class="pt-button-grid">
            <div>
                <button on:click="{ () => setProperty('warehouse') }" class:opacity-100={propertyType === 'warehouse'} class:text-white={propertyType === 'warehouse'} class:bg-axis-blue={propertyType === 'warehouse'}>
                    <img src="/wp-content/themes/axis-solar-corporate/images/warehouse.jpg" class="w-full h-24 object-fit" alt="Warehouse" />
                    <span>Warehouse</span>
                </button>
            </div>
            <div>
                <button on:click="{ () => setProperty('office') }" class:opacity-100={propertyType === 'office'} class:text-white={propertyType === 'office'} class:bg-axis-blue={propertyType === 'office'}>
                    <img src="/wp-content/themes/axis-solar-corporate/images/office.jpg" class="w-full h-24 object-fit" alt="Office" />
                    <span>Office</span>
                </button>
            </div>
            <div>
                <button on:click="{ () => setProperty('school') }" class:opacity-100={propertyType === 'school'} class:text-white={propertyType === 'school'} class:bg-axis-blue={propertyType === 'school'}>
                    <img src="/wp-content/themes/axis-solar-corporate/images/school.jpg" class="w-full h-24 object-fit" alt="School" />
                    <span>School</span>
                </button>
            </div>
            <div>
                <button on:click="{ () => setProperty('multifamily') }" class:opacity-100={propertyType === 'multifamily'} class:text-white={propertyType === 'multifamily'} class:bg-axis-blue={propertyType === 'multifamily'}>
                    <img src="/wp-content/themes/axis-solar-corporate/images/multifamily.jpg" class="w-full h-24 object-fit" alt="Multifamily" />
                    <span>Multifamily</span>
                </button>
            </div>
            <div>
                <button on:click="{ () => setProperty('land') }" class:opacity-100={propertyType === 'land'} class:text-white={propertyType === 'land'} class:bg-axis-blue={propertyType === 'land'}>
                    <img src="/wp-content/themes/axis-solar-corporate/images/land.jpg" class="w-full h-24 object-fit" alt="Land" />
                    <span>Building &plus; Land</span>
                </button>
            </div>
        </div>
    </div>

    <!-- Average Monthly Bill -->
    <div id="sv-questionnaire-average-monthly-bill" class="p-4 block bg-white mb-4">
        <label for="monthly-bill">What is your average Monthly Bill?</label>

        <div class="range w-full">
            <div class="range-slider">
                <input id="monthly-bill" type="range" step="1" min="1" max="5" bind:value="{averageMonthlyBill}" class="range-input" />
                <div class="range-labels">
                    <span class="bg-gray-100 p-2">Less than $1000</span>
                    <span class="hidden md:block p-2"></span>
                    <span class="p-2 font-bold w-auto text-center">{ averageMonthlyBillLabels[averageMonthlyBill - 1] }</span>
                    <span class="hidden md:block p-2"></span>
                    <span class="bg-gray-100 p-2">$10000+</span>
                </div>
            </div>
        </div>
    </div>

    <!-- Energy Provider -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div id="sv-questionnaire-provider">
            <label for="provider">Who is your Energy/Utility Provider?</label>

            <select name="provider" id="provider" class="w-full pricing-table" bind:value={ energyProvider }>
                <option>Austin Energy</option>
                <option>CPS</option>
                <option>PEC</option>
                <option>Deregulated</option>
                <option>Unsure</option>
            </select>
        </div>

        <div id="sv-questionnaire-profit-status">
            <label for="profit-status">Is this a For-Profit or Non-Profit organization?</label>

            <select name="profit-status" id="profit-status" class="w-full pricing-table" bind:value={ profitStatus }>
                <option>For-Profit</option>
                <option>Non-Profit</option>
            </select>
        </div>

        <div class="p-4 md:col-span-2 text-right">
            <button on:click={ () => onSubmit() } class="submit">Submit</button>
        </div>
    </div>

    {#if response.suitable_for_solar != null}
    <div class="results my-4 font-normal" id="sv-pricing-results">
        <div class="w-full grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div class="bg-gray-100 text-gray-900 text-center text-3xl p-4">
                <span class="text-xs text-gray-400 block">Payback Period</span>
                <span class="font-bold">{ response.payback_period } Years</span>
            </div>

            <div class="bg-gray-100 text-gray-900 text-center text-3xl p-4">
                <span class="text-xs text-gray-400 block">Monthly Energy Savings</span>
                <span class="font-bold">${ response.monthly_energy_savings }</span>
            </div>

            <div class="bg-gray-100 text-gray-900 text-center text-3xl p-4">
                <span class="text-xs text-gray-400 block">Net Investment</span>
                <span class="font-bold">${ response.net_investment }</span>
            </div>

            <table class="mb-4 table-fixed w-full mx-auto border border-axis">
                <caption class="caption-top text-lg font-bold text-white bg-axis-blue p-4">
                    System Information
                </caption>
                <tbody class="text-left divide-y divide-dashed text-sm">
                <tr class="bg-white">
                    <th class="p-2 font-normal">Is your business suitable for Solar?</th>
                    <td class="p-2">{ response.suitable_for_solar }</td>
                </tr>
                <tr class="bg-slate-50">
                    <th class="p-2 font-normal">Example System Size</th>
                    <td class="p-2">{ response.example_system_size }kW</td>
                </tr>
                <tr class="bg-white">
                    <th class="p-2 font-normal">Total Number of Solar Panels</th>
                    <td class="p-2">{ response.total_number_of_panels }</td>
                </tr>
                </tbody>
            </table>

            <table class="mb-4 table-fixed w-full mx-auto border border-axis">
                <caption class="caption-top text-lg font-bold text-white bg-axis-blue p-4">
                    System Financials
                </caption>
                <tbody class="text-left divide-y divide-dashed text-sm">
                <tr class="bg-white">
                    <th class="p-2 font-normal">Estimated System Cost</th>
                    <td class="p-2">${ response.estimated_system_cost }</td>
                </tr>
                <tr class="bg-slate-50">
                    <th class="p-2 font-normal">Estimated Rebate</th>
                    <td class="p-2">${ response.estimated_rebate }</td>
                </tr>
                <tr class="bg-white">
                    <th class="p-2 font-normal">Estimated Federal Investment Tax Credit</th>
                    <td class="p-2">${ response.estimated_fit_credit }</td>
                </tr>
                <tr class="bg-slate-50">
                    <th class="p-2 font-normal">Estimated Accelerated Depreciation</th>
                    <td class="p-2">${ response.estimated_accelerated_deprecation }</td>
                </tr>
                <tr class="bg-white">
                    <th class="p-2 font-normal">Net Investment</th>
                    <td class="p-2">${ response.net_investment }</td>
                </tr>
                </tbody>
            </table>

            <table class="mb-4 table-fixed w-full mx-auto border border-axis">
                <caption class="caption-top text-lg font-bold text-white bg-axis-blue p-4">
                    Savings Analysis
                </caption>
                <tbody class="text-left divide-y divide-dashed text-sm">
                <tr class="bg-white">
                    <th class="p-2 font-normal">Annual Energy Savings</th>
                    <td class="p-2">${ response.annual_energy_savings }</td>
                </tr>
                <tr class="bg-slate-50">
                    <th class="p-2 font-normal">Monthly Energy Savings</th>
                    <td class="p-2">${ response.monthly_energy_savings }</td>
                </tr>
                <tr class="bg-white">
                    <th class="p-2 font-normal">Lifetime energy savings</th>
                    <td class="p-2">${ response.lifetime_energy_savings }</td>
                </tr>
                <tr class="bg-slate-50">
                    <th class="p-2 font-normal">Avg Cost Solar Power ($/kwh)</th>
                    <td class="p-2">${ response.avg_cost_solar_power }</td>
                </tr>
                <tr class="bg-white">
                    <th class="p-2 font-normal">Current power costs ($/kwh)</th>
                    <td class="p-2">${ response.current_power_costs }</td>
                </tr>
                <tr class="bg-slate-50">
                    <th class="p-2 font-normal">Payback Period</th>
                    <td class="p-2">{ response.payback_period } Years</td>
                </tr>
                </tbody>
            </table>
        </div>

        <div class="w-full grid grid-cols-4 gap-0 mb-4 border-t-4 border-b-4 border-axis" id="sv-after-pricing-tables">
            <div class="border-0 w-full min-h-48 h-auto">
                <img src="/wp-content/themes/axis-solar-corporate/images/headshot.jpg" class="mt-12 overflow-hidden w-full object-contain" alt="Headshot of Aubrey Lethbridge" />
            </div>

            <div class="col-span-3 bg-white border-axis w-full min-h-48 h-auto flex items-start justify-start">
                <div class="mt-4 p-8" id="sv-after-pricing-tables-text">
                    <h3>Interested in learning more about how solar can benefit your business?</h3>

                    <p class="leading-6 mb-4 text-sm">The next step is to talk directly with Aubrey Lethbridge, who leads our Business Development. Aubrey has 6+ years of experience in the solar industry and has developed projects across the country. The Axis team combined brings more than 50 years of knowledge and expertise to bear and has quickly become one of the largest commercial solar installers in Texas.</p>

                    <p class="leading-6 mb-4 text-sm">With just a few key pieces of information about your facility and energy bills, the Development team will put together a full, customized, and free analysis of solar for your business. It will provide system size, production numbers, tax benefits, electric bill savings, payback period, IRR, NPV and more.</p>

                    <p class="leading-6 mb-12 text-sm">Click the link below to schedule a 15 minute phone conversation directly with Aubrey. It could be worth millions of dollars to your business.</p>

                    <a href="https://outlook.office.com/bookwithme/user/96a0d01705d942df9f784b57e6f94b87%40axissolar.com/meetingtype/fbd24f0e-abdd-4c7e-8231-4450d7b0e2b2?anonymous" class="px-4 py-2 uppercase font-semibold text-sm tracking-wider bg-axis-blue border text-white hover:bg-white hover:text-axis-blue border-axis-blue">Schedule a Call</a>
                </div>
            </div>
        </div>
    </div>
    {/if}
</div>